import { useEffect, useState } from "react";
import axios from "axios";
import { motion, AnimatePresence } from "framer-motion";
import { toast } from "sonner";
import {
  X, Users, Plus, Loader2, Copy, Link as LinkIcon, CheckCircle2, LogOut, Radio,
  Mic, MicOff, Volume2, VolumeX, Wifi, WifiOff,
} from "lucide-react";
import useEscapeClose from "@/lib/useEscapeClose";
import { toastErrorWithHelp } from "@/lib/toastErrorWithHelp";
import { safeApiError } from "@/lib/safeErrorText";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const OVERLAY_FADE = { opacity: 0 };
const OVERLAY_FULL = { opacity: 1 };
const PANEL_INIT   = { opacity: 0, y: 16 };
const PANEL_IN     = { opacity: 1, y: 0 };

/**
 * CollabModal — Create / join / leave a real-time collab room.
 *
 *   <CollabModal open onClose roomId userName status peers
 *                onJoin={(id,name)=>...} onLeave={()=>...} />
 *
 * `roomId` is the SOURCE of truth — when it's truthy, this modal shows the
 * in-room "Connected" view (peers list, copy link, leave button). When it's
 * falsy, it shows the join/create form.
 *
 * The actual connection lifecycle is managed by `useCollab` in the parent.
 * This modal is purely the UI surface for that state.
 */
export default function CollabModal({
  open, onClose, onOpen,
  roomId, userName, status, peers, error,
  onJoin, onLeave, onChangeName,
  mesh, crdt,
}) {
  const [roomInput, setRoomInput] = useState("");
  const [nameInput, setNameInput] = useState(userName || "");
  const [creating, setCreating] = useState(false);
  const [copied, setCopied] = useState(false);

  useEscapeClose(open, onClose);

  // Sync the local name field with the parent's stored name on open.
  useEffect(() => {
    if (open) setNameInput(userName || "");
  }, [open, userName]);

  // Auto-fill the join-room input with the URL-provided room id so an
  // invitee landing via a shared link only needs to enter their display
  // name + click Join. Prior behaviour required pasting the URL again.
  useEffect(() => {
    if (open && roomId && !roomInput) setRoomInput(roomId);
  }, [open, roomId, roomInput]);

  // If we have a roomId from the URL but the modal is closed, open it
  // automatically so the invitee sees the Join CTA without needing to
  // discover the collab indicator. We only do this on the FIRST mount
  // after a URL load — subsequent state changes are not auto-opened.
  useEffect(() => {
    if (!roomId || status === "connected") return;
    onOpen?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const isConnected = roomId && status === "connected";
  const shareUrl = roomId && typeof window !== "undefined"
    ? `${window.location.origin}/?collab=${encodeURIComponent(roomId)}`
    : "";

  const handleCreate = async () => {
    if (!nameInput.trim()) {
      toast.error("Pick a display name first");
      return;
    }
    setCreating(true);
    try {
      const { data } = await axios.post(`${API}/collab/room`, {}, { timeout: 6000 });
      onChangeName?.(nameInput.trim());
      onJoin?.(data.id, nameInput.trim());
    } catch (e) {
      toastErrorWithHelp("Could not create collab room", {
        description: safeApiError(e),
        probe: "websocket",
      });
    } finally {
      setCreating(false);
    }
  };

  const handleJoin = async () => {
    if (!nameInput.trim()) {
      toast.error("Pick a display name first");
      return;
    }
    const rid = roomInput.trim();
    if (!rid) {
      toast.error("Paste a room ID or share link");
      return;
    }
    // Accept either a raw id or a full /?collab=XYZ URL.
    let parsed = rid;
    try {
      const u = new URL(rid);
      const param = u.searchParams.get("collab");
      if (param) parsed = param;
    } catch (e) { void e; /* not a URL, that's fine */ }
    onChangeName?.(nameInput.trim());
    onJoin?.(parsed, nameInput.trim());
  };

  const handleCopy = async () => {
    if (!shareUrl) return;
    try {
      await navigator.clipboard.writeText(shareUrl);
      setCopied(true);
      setTimeout(() => setCopied(false), 1800);
    } catch (e) {
      toast.error("Copy failed", { description: e.message });
    }
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          key="collabmodal-overlay"
          initial={OVERLAY_FADE}
          animate={OVERLAY_FULL}
          exit={OVERLAY_FADE}
          onClick={onClose}
          data-testid="collab-modal"
          className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4 backdrop-blur-md"
        >
          <motion.div
            initial={PANEL_INIT}
            animate={PANEL_IN}
            exit={PANEL_INIT}
            transition={{ type: "spring", stiffness: 240, damping: 26 }}
            onClick={(e) => e.stopPropagation()}
            className="grid max-h-[90vh] w-full max-w-[560px] grid-rows-[auto_1fr_auto] overflow-hidden rounded-3xl border border-white/10 bg-[#0b0e1c] text-white shadow-2xl"
          >
            {/* Header */}
            <div className="flex items-center justify-between border-b border-white/10 px-5 py-4">
              <div className="flex items-center gap-3">
                <div className="grid h-9 w-9 place-items-center rounded-xl bg-gradient-to-br from-sky-500/40 to-fuchsia-500/40">
                  <Users className="h-4 w-4 text-white" />
                </div>
                <div>
                  <h2 className="font-display text-lg font-bold leading-none">Real-time Collab</h2>
                  <p className="mt-1 font-mono text-[10px] uppercase tracking-[0.18em] text-white/45">
                    {isConnected
                      ? `Live · ${peers.length + 1} member${peers.length === 0 ? "" : "s"}`
                      : "Co-mix in real time · share a link · no audio streamed"}
                  </p>
                </div>
              </div>
              <button
                type="button"
                onClick={onClose}
                data-testid="collab-modal-close-btn"
                className="rounded-xl border border-white/10 bg-white/[0.06] p-2 text-white/70 transition hover:bg-white/[0.12]"
              >
                <X className="h-4 w-4" />
              </button>
            </div>

            {/* Body */}
            <div className="overflow-y-auto px-5 py-5">
              {isConnected ? (
                /* ─── In-room state ─── */
                <div data-testid="collab-room-connected" className="space-y-4">
                  <div className="rounded-2xl border border-emerald-400/40 bg-emerald-500/[0.08] p-4">
                    <div className="mb-1 flex items-center gap-2">
                      <Radio className="h-4 w-4 text-emerald-300" />
                      <p className="font-display text-[14px] font-semibold text-emerald-100">You're in the room</p>
                    </div>
                    <p className="font-body text-[12px] leading-5 text-emerald-100/75">
                      Mixer + transport sync via WebSocket. <strong>Voice & low-latency state</strong> ride a WebRTC peer mesh —
                      click <em>Talk</em> below to broadcast your mic to everyone. No audio is streamed unless you choose to.
                    </p>
                  </div>

                  {/* WebRTC mesh control bar — Talk + connection summary */}
                  {mesh ? (
                    <div
                      data-testid="collab-mesh-bar"
                      className="flex flex-wrap items-center justify-between gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] p-3"
                    >
                      <div className="flex items-center gap-2.5">
                        <button
                          type="button"
                          data-testid="collab-talk-btn"
                          aria-pressed={mesh.localMicEnabled}
                          onClick={async () => {
                            try {
                              await mesh.toggleMic();
                            } catch (e) {
                              toastErrorWithHelp("Microphone error", {
                                description: e?.message,
                                probe: "media",
                              });
                            }
                          }}
                          className={`flex items-center gap-2 rounded-full px-3.5 py-1.5 font-mono text-[11px] uppercase tracking-[0.18em] transition ${
                            mesh.localMicEnabled
                              ? "bg-rose-500/25 text-rose-100 ring-1 ring-rose-400/50 shadow-[0_0_18px_rgba(244,63,94,0.35)]"
                              : "border border-white/10 bg-white/[0.04] text-white/75 hover:bg-white/[0.1]"
                          }`}
                        >
                          {mesh.localMicEnabled ? (
                            <><Mic className="h-3.5 w-3.5" /> Live · Talking</>
                          ) : (
                            <><MicOff className="h-3.5 w-3.5" /> Talk</>
                          )}
                        </button>
                        <span className="font-mono text-[10px] uppercase tracking-wider text-white/45" data-testid="collab-mesh-status">
                          Mesh · {mesh.peerCount || 0} peer{mesh.peerCount === 1 ? "" : "s"}
                          {mesh.localStreamErr ? <span className="ml-1 text-rose-200"> · mic blocked</span> : null}
                        </span>
                      </div>
                      <span className="font-mono text-[9.5px] uppercase tracking-wider text-white/40">
                        STUN only · no TURN
                      </span>
                    </div>
                  ) : null}

                  {/* Yjs CRDT sync status — green when convergence is observed,
                      amber when the doc is local-only (room of one or fresh join).
                      The "fast" sub-badge appears once any update has shipped
                      via the WebRTC data-channel path (Phase 2.5). */}
                  {crdt ? (
                    <div
                      data-testid="collab-crdt-bar"
                      className="flex items-center justify-between gap-3 rounded-2xl border border-white/[0.07] bg-white/[0.025] px-3 py-2"
                    >
                      <div className="flex items-center gap-2">
                        <span
                          data-testid="collab-crdt-status"
                          className={`grid h-2 w-2 place-items-center rounded-full ${
                            crdt.sync ? "bg-emerald-300 shadow-[0_0_10px_rgba(52,211,153,0.7)]" : "bg-amber-300/70"
                          }`}
                        />
                        <span className="font-mono text-[10px] uppercase tracking-[0.18em] text-white/75">
                          CRDT · {crdt.sync ? "synced" : (mesh && mesh.peerCount > 0 ? "syncing" : "idle")}
                        </span>
                        {crdt.openChannels > 0 ? (
                          <span
                            data-testid="collab-crdt-fast-badge"
                            title={`RTC carrying ${crdt.stats?.rtcSent || 0} of ${(crdt.stats?.rtcSent || 0) + (crdt.stats?.wsSent || 0)} outbound updates`}
                            className="rounded-full bg-fuchsia-500/20 px-1.5 py-0.5 font-mono text-[9px] uppercase tracking-wider text-fuchsia-100 ring-1 ring-fuchsia-400/40"
                          >
                            Fast · RTC
                          </span>
                        ) : null}
                      </div>
                      <span className="font-mono text-[9.5px] uppercase tracking-wider text-white/40" data-testid="collab-crdt-stats">
                        {crdt.stats
                          ? `${crdt.stats.rtcSent + crdt.stats.wsSent} sent · ${crdt.stats.rtcRecv + crdt.stats.wsRecv} recv`
                          : "Yjs · transport + mixer"}
                      </span>
                    </div>
                  ) : null}

                  <div>
                    <label className="mb-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Invite link</label>
                    <div className="flex items-stretch gap-2">
                      <input
                        readOnly
                        value={shareUrl}
                        data-testid="collab-share-url"
                        onFocus={(e) => e.target.select()}
                        className="flex-1 rounded-xl border border-white/10 bg-black/40 px-3 py-2 font-mono text-[12px] text-white"
                      />
                      <button
                        type="button"
                        onClick={handleCopy}
                        data-testid="collab-copy-btn"
                        className={`flex items-center gap-1.5 rounded-xl px-3 py-2 font-mono text-[10px] uppercase tracking-wider transition ${
                          copied
                            ? "bg-emerald-500/30 text-emerald-100 ring-1 ring-emerald-400/50"
                            : "bg-fuchsia-500/25 text-fuchsia-100 ring-1 ring-fuchsia-400/40 hover:bg-fuchsia-500/40"
                        }`}
                      >
                        {copied ? <><CheckCircle2 className="h-3 w-3" />Copied</> : <><Copy className="h-3 w-3" />Copy</>}
                      </button>
                    </div>
                  </div>

                  <div>
                    <p className="mb-2 font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">
                      Members ({peers.length + 1})
                    </p>
                    <ul className="space-y-1.5" data-testid="collab-members">
                      {/* The room sees Alice as Alice; we show "you" badge so she knows
                          which avatar is hers. */}
                      <li className="flex items-center gap-3 rounded-lg border border-white/10 bg-white/[0.04] px-3 py-2">
                        <span className="grid h-7 w-7 place-items-center rounded-full bg-fuchsia-500/30 font-display text-[12px] font-bold text-fuchsia-100 ring-1 ring-fuchsia-400/30">
                          {(userName || "?").slice(0, 1).toUpperCase()}
                        </span>
                        <span className="flex-1 font-display text-[13px] text-white">{userName || "Anonymous"}</span>
                        <span className="rounded-full bg-fuchsia-500/20 px-2 py-0.5 font-mono text-[9px] uppercase tracking-wider text-fuchsia-100">you</span>
                      </li>
                      {peers.map((p) => {
                        const state = mesh?.peerStreams?.[p.id];
                        const audible = state?.hasRemoteAudio && !state?.muted;
                        const connOk = state?.connectionState === "connected" || state?.connectionState === "completed";
                        return (
                          <li
                            key={`peer-${p.id}`}
                            data-testid={`collab-peer-${p.id}`}
                            className="flex items-center gap-3 rounded-lg border border-white/[0.07] bg-white/[0.02] px-3 py-2"
                          >
                            <span
                              className="grid h-7 w-7 place-items-center rounded-full font-display text-[12px] font-bold text-white ring-1 ring-white/15"
                              style={{ background: peerColor(p.id) }}
                            >
                              {p.name.slice(0, 1).toUpperCase()}
                            </span>
                            <span className="flex-1 truncate font-display text-[13px] text-white/85">{p.name}</span>
                            {mesh ? (
                              <span
                                data-testid={`collab-peer-${p.id}-rtc`}
                                title={`RTC: ${state?.connectionState || "—"}${state?.rttMs ? ` · ${state.rttMs} ms RTT` : ""}`}
                                className={`grid h-6 min-w-[1.5rem] place-items-center gap-1 rounded-full px-1.5 ${
                                  connOk
                                    ? "bg-emerald-500/20 text-emerald-200 ring-1 ring-emerald-400/40"
                                    : "bg-white/[0.05] text-white/45 ring-1 ring-white/10"
                                }`}
                              >
                                {connOk ? <Wifi className="h-3 w-3" /> : <WifiOff className="h-3 w-3" />}
                                {state?.rttMs ? (
                                  <span data-testid={`collab-peer-${p.id}-rtt`} className="font-mono text-[9px] tabular-nums">
                                    {state.rttMs}ms
                                  </span>
                                ) : null}
                              </span>
                            ) : null}
                            {mesh && state?.hasRemoteAudio ? (
                              <button
                                type="button"
                                data-testid={`collab-peer-${p.id}-mute`}
                                aria-pressed={!!state.muted}
                                onClick={() => mesh.setPeerMuted(p.id, !state.muted)}
                                title={state.muted ? "Unmute this peer" : "Mute this peer"}
                                className={`grid h-6 w-6 place-items-center rounded-full transition ${
                                  audible
                                    ? "bg-sky-500/20 text-sky-100 ring-1 ring-sky-400/40 hover:bg-sky-500/35"
                                    : "bg-white/[0.05] text-white/55 ring-1 ring-white/10 hover:bg-white/[0.1]"
                                }`}
                              >
                                {state.muted ? <VolumeX className="h-3 w-3" /> : <Volume2 className="h-3 w-3" />}
                              </button>
                            ) : null}
                          </li>
                        );
                      })}
                    </ul>
                  </div>
                </div>
              ) : (
                /* ─── Join / create form ─── */
                <div data-testid="collab-form" className="space-y-4">
                  {status === "error" && error && (
                    <div className="rounded-xl border border-rose-400/40 bg-rose-500/[0.08] p-3 font-body text-[12px] text-rose-100">
                      {error}
                    </div>
                  )}
                  {status === "connecting" && (
                    <div className="flex items-center gap-2 rounded-xl border border-fuchsia-400/40 bg-fuchsia-500/[0.06] p-3 font-mono text-[11px] uppercase tracking-wider text-fuchsia-100">
                      <Loader2 className="h-3 w-3 animate-spin" />Connecting…
                    </div>
                  )}

                  <div>
                    <label htmlFor="collab-name" className="mb-1 block font-mono text-[10px] uppercase tracking-[0.18em] text-white/55">Display name</label>
                    <input
                      id="collab-name"
                      data-testid="collab-name-input"
                      value={nameInput}
                      onChange={(e) => setNameInput(e.target.value.slice(0, 32))}
                      placeholder="Your name (shows to other members)"
                      className="w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 text-sm text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                    />
                  </div>

                  <div className="grid gap-3 sm:grid-cols-2">
                    <div className="rounded-2xl border border-fuchsia-400/30 bg-fuchsia-500/[0.05] p-4">
                      <p className="mb-1 flex items-center gap-1.5 font-display text-[13px] font-semibold text-fuchsia-100">
                        <Plus className="h-3.5 w-3.5" />Start a new room
                      </p>
                      <p className="mb-3 font-body text-[11px] leading-4 text-white/55">
                        Generate a fresh link to share with your collaborators.
                      </p>
                      <button
                        type="button"
                        onClick={handleCreate}
                        disabled={creating || !nameInput.trim()}
                        data-testid="collab-create-btn"
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-gradient-to-r from-fuchsia-500 to-sky-500 px-3 py-2 font-display text-[13px] font-semibold text-white hover:opacity-90 disabled:opacity-50"
                      >
                        {creating ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Plus className="h-3.5 w-3.5" />}
                        Create room
                      </button>
                    </div>
                    <div className="rounded-2xl border border-white/[0.08] bg-white/[0.025] p-4">
                      <p className="mb-1 flex items-center gap-1.5 font-display text-[13px] font-semibold text-white">
                        <LinkIcon className="h-3.5 w-3.5" />Join existing room
                      </p>
                      <p className="mb-3 font-body text-[11px] leading-4 text-white/55">
                        Paste a link or room ID from a collaborator.
                      </p>
                      <input
                        value={roomInput}
                        onChange={(e) => setRoomInput(e.target.value)}
                        placeholder="Room link or ID"
                        data-testid="collab-room-input"
                        className="mb-2 w-full rounded-xl border border-white/10 bg-black/40 px-3 py-2 font-mono text-[12px] text-white placeholder:text-white/30 focus:border-fuchsia-400/60 focus:outline-none"
                      />
                      <button
                        type="button"
                        onClick={handleJoin}
                        disabled={!roomInput.trim() || !nameInput.trim()}
                        data-testid="collab-join-btn"
                        className="flex w-full items-center justify-center gap-1.5 rounded-xl bg-white/[0.08] px-3 py-2 font-display text-[13px] font-semibold text-white transition hover:bg-white/[0.16] disabled:opacity-50"
                      >
                        Join →
                      </button>
                    </div>
                  </div>
                  <p className="font-mono text-[9px] uppercase tracking-wider text-white/35">
                    Tip · the link in your address bar updates with the room id so you can refresh without losing the session.
                  </p>
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="flex items-center justify-between gap-2 border-t border-white/10 bg-black/30 px-5 py-3">
              <span className="font-mono text-[10px] uppercase tracking-wider text-white/45">
                {isConnected ? "Connected · live syncing" : "Ephemeral · closes when last member leaves"}
              </span>
              {isConnected ? (
                <button
                  type="button"
                  onClick={() => { onLeave?.(); }}
                  data-testid="collab-leave-btn"
                  className="flex items-center gap-1.5 rounded-xl border border-rose-400/30 bg-rose-500/[0.10] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-rose-100 transition hover:bg-rose-500/[0.20]"
                >
                  <LogOut className="h-3 w-3" />Leave room
                </button>
              ) : (
                <button
                  type="button"
                  onClick={onClose}
                  data-testid="collab-form-close-btn"
                  className="rounded-xl border border-white/10 bg-white/[0.06] px-3 py-1.5 font-mono text-[11px] uppercase tracking-wider text-white/75 hover:bg-white/[0.12]"
                >
                  Cancel
                </button>
              )}
            </div>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}

/** Deterministic color per peer id — used to tint the peer avatar so users
 *  can mentally map "blue avatar" → "Jamie". Same id → same color forever. */
export function peerColor(id) {
  let h = 0;
  for (let i = 0; i < id.length; i++) h = (h * 31 + id.charCodeAt(i)) >>> 0;
  const hue = h % 360;
  return `linear-gradient(160deg, hsl(${hue}, 70%, 45%), hsl(${(hue + 60) % 360}, 70%, 22%))`;
}

/** Tiny inline indicator for the studio header — shows live status + peer count. */
export function CollabIndicator({ status, peers, onClick }) {
  const isLive = status === "connected";
  const count = (peers?.length || 0) + (isLive ? 1 : 0);
  return (
    <button
      type="button"
      data-testid="collab-indicator-btn"
      onClick={onClick}
      title={isLive ? `Collab live · ${count} members` : "Start or join a collab room"}
      className={`studio-pill flex items-center gap-2 rounded-2xl border px-3 py-2 font-mono text-[10px] uppercase tracking-[0.18em] transition ${
        isLive
          ? "border-emerald-400/40 bg-emerald-500/[0.12] text-emerald-100 shadow-[0_0_18px_rgba(52,211,153,0.25)]"
          : "border-white/[0.08] bg-white/[0.025] text-white/65 hover:border-white/20 hover:bg-white/[0.05] hover:text-white/95"
      }`}
    >
      {isLive ? (
        <>
          <span className="relative grid h-2 w-2 place-items-center">
            <span className="absolute inline-flex h-2 w-2 animate-ping rounded-full bg-emerald-300/80" />
            <span className="relative h-1.5 w-1.5 rounded-full bg-emerald-300" />
          </span>
          Live · {count}
        </>
      ) : (
        <>
          <Users className="h-3.5 w-3.5" />Collab
        </>
      )}
    </button>
  );
}
